/**
 * Konstante domena. Vrijednosti (ključevi) su identični enumima u `api/openapi.yaml`;
 * labele su i18n ključevi (namespace `common`), boje su CSS varijable iz `@sm/tokens`.
 */

// ---------------------------------------------------------------- faze projekta
export const PHASE_KEYS = [
  'measuring',
  'approval',
  'production',
  'quality',
  'delivery',
  'installation',
  'done',
] as const;
export type Phase = (typeof PHASE_KEYS)[number];

export interface PhaseDef {
  key: Phase;
  /** Redni broj 1..7 (odgovara tokenu `--ph1..--ph7`). */
  index: number;
  labelKey: `common:phase.${Phase}`;
  /** CSS varijabla boje faze, npr. `var(--ph1)`. */
  colorVar: `--ph${number}`;
  /** Phosphor ikona (bez prefiksa `ph-`). */
  icon: string;
}

const PHASE_ICONS: Record<Phase, string> = {
  measuring: 'ruler',
  approval: 'seal-check',
  production: 'factory',
  quality: 'shield-check',
  delivery: 'truck',
  installation: 'wrench',
  done: 'flag-checkered',
};

export const PHASES: readonly PhaseDef[] = PHASE_KEYS.map((key, i) => ({
  key,
  index: i + 1,
  labelKey: `common:phase.${key}` as const,
  colorVar: `--ph${i + 1}` as const,
  icon: PHASE_ICONS[key],
}));

export function phaseIndex(phase: Phase): number {
  return PHASE_KEYS.indexOf(phase);
}

/** Aktivni projekat = svaka faza osim `done`. */
export function isActivePhase(phase: Phase): boolean {
  return phase !== 'done';
}

// ---------------------------------------------------------------- stanice proizvodnje
export const STATION_KEYS = [
  'cutting',
  'welding',
  'hardware',
  'glazing',
  'quality',
  'packing',
] as const;
export type Station = (typeof STATION_KEYS)[number];

export const STATIONS = STATION_KEYS.map((key, i) => ({
  key,
  order: i + 1,
  labelKey: `common:station.${key}` as const,
}));

export const STATION_STATUSES = ['todo', 'in_progress', 'done', 'blocked'] as const;
export type StationStatus = (typeof STATION_STATUSES)[number];

/** Prag opterećenja stanice: > 85% upozorenje, > 100% opasnost (kao u dizajnu). */
export const STATION_LOAD_THRESHOLDS = { caution: 85, danger: 100 } as const;

export function stationLoadTone(loadPct: number): 'ok' | 'caution' | 'danger' {
  if (loadPct > STATION_LOAD_THRESHOLDS.danger) return 'danger';
  if (loadPct > STATION_LOAD_THRESHOLDS.caution) return 'caution';
  return 'ok';
}

export const PRODUCTION_LIST_KINDS = ['cutting', 'glass', 'hardware', 'shutters'] as const;
export type ProductionListKind = (typeof PRODUCTION_LIST_KINDS)[number];

// ---------------------------------------------------------------- fotografije
export const PHOTO_CATEGORIES = [
  'before_work',
  'inside',
  'outside',
  'detail',
  'obstacle',
  'damage',
] as const;
export type PhotoCategory = (typeof PHOTO_CATEGORIES)[number];

/** Ograničenja fotografija (klijent kompresuje na ~2560 px JPEG q0.8). */
export const PHOTO_LIMITS = {
  maxEdgePx: 2560,
  jpegQuality: 0.8,
  maxBytes: 20 * 1024 * 1024,
  thumbSizes: [320, 1280] as const,
  contentTypes: ['image/jpeg', 'image/png', 'image/heic'] as const,
} as const;

export const LOGO_LIMITS = {
  maxBytes: 2 * 1024 * 1024,
  contentTypes: ['image/png', 'image/jpeg', 'image/svg+xml'] as const,
} as const;

// ---------------------------------------------------------------- uloge i dozvole
export const ROLES = [
  'admin',
  'surveyor',
  'production',
  'quality',
  'installer',
  'director',
] as const;
export type Role = (typeof ROLES)[number];

export const PERMISSIONS = [
  'dashboard.read',
  'projects.read',
  'projects.write',
  'projects.create',
  'positions.write',
  'measurements.submit',
  'measurements.review',
  'photos.read',
  'photos.write',
  'production.read',
  'production.write',
  'documents.read',
  'documents.generate',
  'analytics.read',
  'users.manage',
  'settings.manage',
  'features.manage',
  'clearance_rules.manage',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

/**
 * Matrica dozvola (izvor istine i za Go: `server/internal/auth/permissions.go` mora imati iste vrijednosti,
 * provjerava se testom nad `spec/fixtures/permissions.json`).
 */
export const ROLE_PERMISSIONS: Readonly<Record<Role, readonly Permission[]>> = {
  admin: PERMISSIONS,
  surveyor: [
    'projects.read',
    'projects.create',
    'positions.write',
    'measurements.submit',
    'photos.read',
    'photos.write',
    'documents.read',
  ],
  production: [
    'dashboard.read',
    'projects.read',
    'photos.read',
    'production.read',
    'production.write',
    'documents.read',
  ],
  quality: [
    'dashboard.read',
    'projects.read',
    'photos.read',
    'photos.write',
    'production.read',
    'production.write',
    'documents.read',
  ],
  installer: ['projects.read', 'photos.read', 'photos.write', 'documents.read'],
  director: [
    'dashboard.read',
    'projects.read',
    'photos.read',
    'production.read',
    'documents.read',
    'analytics.read',
  ],
};

export function permissionsFor(role: Role): readonly Permission[] {
  return ROLE_PERMISSIONS[role];
}

export function can(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

// ---------------------------------------------------------------- tipovi otvora i objekata
export const OPENING_TYPE_KEYS = [
  'double_tilt_turn',
  'single_tilt_turn',
  'balcony_door',
  'tilt_turn',
  'fixed',
  'sliding_hs',
] as const;
export type OpeningType = (typeof OPENING_TYPE_KEYS)[number];

export interface OpeningTypeDef {
  key: OpeningType;
  labelKey: `common:openingType.${OpeningType}`;
  /** Broj krila (0 = fiksno). */
  sashes: number;
  /** Da li je vrata (utiče na listu okova i prag). */
  door: boolean;
}

const SASHES: Record<OpeningType, { sashes: number; door: boolean }> = {
  double_tilt_turn: { sashes: 2, door: false },
  single_tilt_turn: { sashes: 1, door: false },
  balcony_door: { sashes: 1, door: true },
  tilt_turn: { sashes: 1, door: false },
  fixed: { sashes: 0, door: false },
  sliding_hs: { sashes: 2, door: true },
};

export const OPENING_TYPES: readonly OpeningTypeDef[] = OPENING_TYPE_KEYS.map((key) => ({
  key,
  labelKey: `common:openingType.${key}` as const,
  ...SASHES[key],
}));

export const BUILDING_TYPES = ['house', 'apartment', 'commercial', 'public'] as const;
export type BuildingType = (typeof BUILDING_TYPES)[number];

// ---------------------------------------------------------------- statusi
export const POSITION_STATUSES = ['draft', 'measured', 'warning', 'confirmed', 'locked'] as const;
export type PositionStatus = (typeof POSITION_STATUSES)[number];

export const MEASUREMENT_STATUSES = [
  'draft',
  'submitted',
  'approved',
  'approved_with_note',
  'returned',
] as const;
export type MeasurementStatus = (typeof MEASUREMENT_STATUSES)[number];

export const DECISIONS = ['approve', 'approve_with_note', 'return'] as const;
export type Decision = (typeof DECISIONS)[number];

export const WARNING_TYPES = ['width_spread', 'height_spread', 'diagonal', 'reveal'] as const;
export type WarningType = (typeof WARNING_TYPES)[number];

export const DOCUMENT_TYPES = ['measurement_protocol', 'production_order'] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];

export const CLEARANCE_RULE_STATUSES = ['active', 'draft'] as const;

// ---------------------------------------------------------------- feature flagovi
export const FEATURE_KEYS = [
  'installation',
  'crm',
  'reports',
  'inventory',
  'shopfloor',
  'lang_de',
  'photo_annotations_advanced',
  'dictation',
  'qr_scan',
  'production',
  'analytics',
] as const;
export type FeatureKey = (typeof FEATURE_KEYS)[number];
/**
 * Moduli koje određuje paket (Osnovni / Profesionalni / Individualno); nisu „Uskoro“ moduli. `off` sakriva
 * stavku menija i API vraća `404 feature_disabled`.
 */
export const PLAN_FEATURE_KEYS = [
  'production',
  'analytics',
] as const satisfies readonly FeatureKey[];
export const FEATURE_STATES = ['off', 'preview', 'on'] as const;
export type FeatureState = (typeof FEATURE_STATES)[number];

/** Stavka menija je vidljiva kad je flag `preview` ili `on`. */
export function featureVisible(state: FeatureState | undefined): boolean {
  return state === 'preview' || state === 'on';
}

/** Funkcija radi samo kad je flag `on`. */
export function featureEnabled(state: FeatureState | undefined): boolean {
  return state === 'on';
}

// ---------------------------------------------------------------- platforma: statusi firme i paketi
export const TENANT_STATUSES = [
  'pending_approval',
  'trial',
  'active',
  'suspended',
  'cancelled',
] as const;
export type TenantStatus = (typeof TENANT_STATUSES)[number];

/** Limiti paketa (`null` = bez ograničenja), redoslijed prikaza u „Podešavanja → Paket“. */
export const PLAN_LIMIT_KEYS = [
  'users',
  'surveyors',
  'projectsPerMonth',
  'storageGb',
  'devices',
] as const;
export type PlanLimitKey = (typeof PLAN_LIMIT_KEYS)[number];

/** Udio iskorištenosti limita (0–1+), `null` kad limita nema. */
export function limitRatio(used: number, limit: number | null | undefined): number | null {
  if (limit == null) return null;
  if (limit <= 0) return used > 0 ? Infinity : 0;
  return used / limit;
}

/** Ton trake iskorištenosti: ≥ 100 % opasnost, ≥ 80 % upozorenje. */
export function limitTone(ratio: number | null): 'ok' | 'caution' | 'danger' {
  if (ratio == null) return 'ok';
  if (ratio >= 1) return 'danger';
  if (ratio >= 0.8) return 'caution';
  return 'ok';
}

/** Najduže trajanje impersonacije (podrška NENASAL), u minutama. */
export const IMPERSONATION_MAX_MINUTES = 30 as const;

// ---------------------------------------------------------------- ostalo
export const CURRENCY = 'BAM' as const;
/** Oznaka valute u UI-ju i dokumentima. */
export const CURRENCY_LABEL = 'KM' as const;
export const SYNC_PROTOCOL_VERSION = 1 as const;
export const PASSWORD_MIN_LENGTH = 12 as const;
/** Podrazumijevani šablon numeracije projekata. */
export const DEFAULT_PROJECT_NUMBER_PATTERN = '{YYYY}-{NNNN}' as const;

/**
 * Formatira šifru projekta po šablonu tenanta.
 * `formatProjectCode('{YYYY}-{NNNN}', 2026, 284)` -> `2026-0284`.
 */
export function formatProjectCode(pattern: string, year: number, counter: number): string {
  return pattern
    .replace(/\{YYYY\}/g, String(year).padStart(4, '0'))
    .replace(/\{YY\}/g, String(year % 100).padStart(2, '0'))
    .replace(/\{(N+)\}/g, (_m, n: string) => String(counter).padStart(n.length, '0'));
}

/** Šifra naloga za proizvodnju, npr. `NP-0283`. */
export function formatOrderCode(counter: number): string {
  return `NP-${String(counter).padStart(4, '0')}`;
}
